import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// vetobase.com website analytics (PostHog, read through our backend).
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('sys_token') || ''}` } });
const N = (v) => Number(v || 0).toLocaleString();
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const ago = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const PALETTE = ['#111111', '#0F6E56', '#2F6FD6', '#C2571A', '#7A3FB0', '#C7363A', '#1F8F8A', '#8A6A12', '#5B5B5B', '#B0477A'];
const CTA_LABEL = {
  watch_demo: 'Watch the demo', go_to_workspace: 'Go to workspace', contact_cta: 'Contact', pricing_cta: 'Pricing',
  email_click: 'Email link', phone_click: 'Phone link', whatsapp_click: 'WhatsApp link', use_case_link: 'Use-case link', blog_link: 'Blog link',
};
const VIDEO_LABEL = { play: 'Started playing', unmute: 'Turned sound on', watched_25: 'Watched 25%', watched_50: 'Watched 50%', watched_75: 'Watched 75%', watched_100: 'Watched to the end', zoom: 'Zoomed in', share: 'Shared', download: 'Downloaded', left: 'Left the page' };
const EVENT_LABEL = (e) => ({
  $pageview: `Viewed ${e.path || '/'}`, $pageleave: `Left ${e.path || '/'}`, watch_demo_click: `Clicked “Watch the demo” (${e.placement || '—'})`,
  cta_click: `Clicked ${CTA_LABEL[e.cta] || e.cta} (${e.placement || '—'})`, demo_video: `Demo video: ${VIDEO_LABEL[e.action] || e.action}`,
  contact_form_started: 'Started the contact form', contact_form_field_filled: `Filled “${e.field}”`, contact_form_submitted: 'Submitted the contact form',
  contact_form_abandoned: `Left the contact form (${(safeArr(e.fields_filled)).join(', ') || 'nothing filled'})`, contact_form_error: 'Contact form error',
  $autocapture: `Clicked “${e.el_text || 'element'}”`,
}[e.event] || e.event);
// "Lahore, Punjab, Pakistan" (region skipped when it repeats the city).
const place = (r) => [r.city, r.region && r.region !== r.city ? r.region : null, r.country].filter(Boolean).join(', ');
const Where = ({ r }) => (<>{place(r) || '—'}{r.radius ? <div className="sa-dim">within ~{r.radius} km</div> : null}</>);

// Location + network details for one visitor (IP geolocation from PostHog/MaxMind,
// provider from our /network lookup). Approximate by nature: city-level at best.
function LocationPanel({ row }) {
  const [netInfo, setNetInfo] = useState(null);
  useEffect(() => {
    let live = true;
    setNetInfo(null);
    if (!row.ip) return undefined;
    axios.get(`${API_URL}/api/authorized-admin/site-analytics/network/${encodeURIComponent(row.ip)}`, auth())
      .then((r) => { if (live) setNetInfo(r.data); }).catch(() => { if (live) setNetInfo({}); });
    return () => { live = false; };
  }, [row.ip]);
  const lat = Number(row.lat), lng = Number(row.lng);
  const hasPin = row.lat != null && row.lng != null && Number.isFinite(lat) && Number.isFinite(lng);
  const km = Math.min(300, Math.max(5, Number(row.radius) || 25));
  const dLat = km / 111, dLng = km / (111 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
  const bbox = [lng - dLng, lat - dLat, lng + dLng, lat + dLat].map((v) => v.toFixed(4)).join(',');
  const map = hasPin ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}` : '';
  const rows = [
    ['City', row.city], ['Province / state', row.region], ['Country', row.country], ['Postcode', row.postal], ['Time zone', row.tz],
    ['Approx. point', hasPin ? `${lat.toFixed(4)}, ${lng.toFixed(4)}${row.radius ? ` (± ${row.radius} km)` : ''}` : null],
    ['Internet provider', netInfo ? (netInfo.isp || '—') : 'Looking up…'],
    ['Network', netInfo && netInfo.asn ? [netInfo.asn, netInfo.prefix].filter(Boolean).join(' · ') : null],
    ['Hostname', netInfo && netInfo.host],
    ['Device', [row.device, row.os, row.browser].filter(Boolean).join(' · ')],
  ].filter(([, v]) => v);
  return (
    <div className="sa-loc">
      <div className="sa-mini-h">Location &amp; network</div>
      {netInfo && netInfo.kind === 'mobile' && <div className="sa-loc-warn">Mobile data: operators route traffic through hub cities, so the city may not be where the visitor is.</div>}
      {netInfo && netInfo.kind === 'hosting' && <div className="sa-loc-warn">Data centre / VPN: this is the server's location, not the visitor's.</div>}
      <dl className="sa-loc-grid">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
      {hasPin && (
        <>
          <iframe className="sa-map" title="Approximate visitor location" src={map} loading="lazy" />
          <div className="sa-loc-foot">
            <span>The pin is the centre of the area this IP maps to{row.radius ? `; the visitor is somewhere within ~${row.radius} km of it` : ''}. Street or block level is not available from an IP address.</span>
            <a href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank" rel="noreferrer">Open in Google Maps ↗</a>
          </div>
        </>
      )}
    </div>
  );
}

function safeArr(v) { if (Array.isArray(v)) return v; try { const x = JSON.parse(v); return Array.isArray(x) ? x : []; } catch { return []; } }

function Donut({ items, total, onPick, active }) {
  const R = 74, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg width="200" height="200" viewBox="0 0 200 200" className="sa-donut">
      <circle cx="100" cy="100" r={R} fill="none" stroke="#EFE9DF" strokeWidth="30" />
      {items.map((it, i) => {
        const frac = total ? it.visitors / total : 0;
        const dash = frac * C;
        const el = (
          <circle key={it.country} cx="100" cy="100" r={R} fill="none" stroke={PALETTE[i % PALETTE.length]} strokeWidth={active === it.country ? 36 : 30}
            strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc} transform="rotate(-90 100 100)" style={{ cursor: 'pointer', transition: 'stroke-width .15s' }}
            onClick={() => onPick(it.country)} />
        );
        acc += dash;
        return el;
      })}
      <text x="100" y="98" textAnchor="middle" className="sa-donut-n">{N(total)}</text>
      <text x="100" y="118" textAnchor="middle" className="sa-donut-l">visitors</text>
    </svg>
  );
}

const Card = ({ title, sub, right, children, className = '' }) => (
  <section className={`sa-card ${className}`}>
    {(title || right) && <div className="sa-card-h"><div><h3>{title}</h3>{sub && <p>{sub}</p>}</div>{right}</div>}
    {children}
  </section>
);

export default function SiteAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState(null);
  const [page, setPage] = useState(1);
  const [country, setCountry] = useState('');
  const [person, setPerson] = useState(null); // { row, events }

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { const r = await axios.get(`${API_URL}/api/authorized-admin/site-analytics/overview?days=${days}`, auth()); setData(r.data); }
    catch (e) { setErr(e.response?.data?.message || 'Could not load analytics'); }
    finally { setLoading(false); }
  }, [days]);
  const loadUsers = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/api/authorized-admin/site-analytics/users?days=${days}&page=${page}`, auth()); setUsers(r.data); }
    catch { setUsers({ total: 0, rows: [], page: 1, size: 25, listed: 0 }); }
  }, [days, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { setPage(1); setCountry(''); }, [days]);

  const openPerson = async (row) => {
    setPerson({ row, events: null });
    try { const r = await axios.get(`${API_URL}/api/authorized-admin/site-analytics/person/${row.person_id}`, auth()); setPerson({ row, events: r.data }); }
    catch { setPerson({ row, events: [] }); }
  };

  const geo = useMemo(() => {
    if (!data) return { top: [], other: [], total: 0 };
    const all = data.countries || [];
    const total = all.reduce((s, c) => s + c.visitors, 0);
    const top = all.slice(0, 7);
    const other = all.slice(7);
    if (other.length) top.push({ country: 'Other', visitors: other.reduce((s, c) => s + c.visitors, 0) });
    return { top, other, total };
  }, [data]);

  if (err) return <div className="sa-empty">{err}</div>;
  if (!data) return <div className="sa-empty">Loading website analytics…</div>;

  const k = data.kpi, f = data.funnel;
  const funnel = [
    ['Visited the site', f.viewed], ['Clicked “Watch the demo”', f.clicked_demo], ['Played the demo video', f.played_demo],
    ['Watched 50%+ of the demo', f.watched_half], ['Clicked “Go to workspace”', f.went_to_app], ['Started the contact form', f.form_started], ['Submitted the contact form', f.form_submitted],
  ];
  const srcMax = Math.max(1, ...data.sources.map((s) => s.visitors));
  const cities = country && country !== 'Other' ? data.cities.filter((c) => c.country === country) : [];
  const videoMap = Object.fromEntries((data.video || []).map((v) => [v.action, v]));
  const ctaRows = (data.ctas || []).filter((c) => c.cta);
  const formStats = { started: data.forms.length, submitted: data.forms.filter((x) => x.submitted).length };
  formStats.left = formStats.started - formStats.submitted;

  return (
    <div className="sa">
      <div className="sa-toolbar">
        <div><h2 className="sa-title">vetobase.com analytics</h2><p className="sa-sub">Visitors, clicks and the contact form on the marketing site · live from PostHog</p></div>
        <div className="sa-tools">
          <select className="sa-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>Today</option><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={365}>12 months</option>
          </select>
          <button className="sa-refresh" onClick={() => { load(); loadUsers(); }} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <div className="sa-kpis">
        {[['Active now', k.active_now, 'last 5 min'], ['Unique visitors', k.visitors, `last ${data.days} days`], ['Page views', k.pageviews, `last ${data.days} days`], ['Page views today', k.pageviews_today, 'so far'],
          ['Watch-demo clicks', k.demo_clicks, `${N(k.demo_clickers)} visitors`], ['Contact forms sent', k.form_submitters, `${N(formStats.started)} started`]].map(([t, v, s]) => (
          <div key={t} className="sa-kpi"><span>{t}</span><b>{N(v)}</b><em>{s}</em></div>
        ))}
      </div>

      <div className="sa-grid2">
        <Card title="Visitor funnel" sub={`distinct visitors · last ${data.days} days`}>
          <div className="sa-funnel">
            {funnel.map(([label, v]) => (
              <div key={label} className="sa-frow">
                <span className="sa-flabel">{label}</span>
                <div className="sa-fbar"><div style={{ width: `${Math.max(pct(v, f.viewed), v ? 6 : 0)}%` }}><b>{N(v)}</b></div></div>
                <span className="sa-fpct">{pct(v, f.viewed)}%</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Visitors by country" sub="Click a slice or country to see its cities.">
          {geo.total === 0 ? <div className="sa-none">No visitors yet in this period.</div> : (
            <div className="sa-geo">
              <Donut items={geo.top} total={geo.total} onPick={(c) => setCountry(country === c ? '' : c)} active={country} />
              <div className="sa-geo-list">
                {geo.top.map((c, i) => (
                  <button key={c.country} className={`sa-geo-row ${country === c.country ? 'on' : ''}`} onClick={() => setCountry(country === c.country ? '' : c.country)}>
                    <i style={{ background: PALETTE[i % PALETTE.length] }} /> <span>{c.country}</span><b>{N(c.visitors)}</b><em>{pct(c.visitors, geo.total)}%</em>
                  </button>
                ))}
              </div>
            </div>
          )}
          {country === 'Other' && geo.other.length > 0 && <p className="sa-note">“Other” = {geo.other.map((c) => `${c.country} ${c.visitors}`).join(' · ')}</p>}
          {cities.length > 0 && (
            <div className="sa-cities"><div className="sa-cities-h">Cities in {country}</div>
              {cities.slice(0, 12).map((c) => <div key={c.city + c.region} className="sa-city"><span>{c.city}{c.region && c.region !== c.city ? <em className="sa-dim"> · {c.region}</em> : null}</span><b>{N(c.visitors)}</b></div>)}
            </div>
          )}
        </Card>
      </div>

      <div className="sa-grid2">
        <Card title="Where visitors come from" sub="first touch · referrer + UTM tag · no referrer = direct">
          {data.sources.length === 0 ? <div className="sa-none">No visitors yet.</div> : data.sources.map((s) => (
            <div key={s.source} className="sa-src">
              <div className="sa-src-h"><b>{s.source}</b><span>{N(s.visitors)} visitors · {N(s.demo)} clicked demo · {N(s.submitted)} sent form</span></div>
              <div className="sa-src-bar"><div style={{ width: `${Math.max(2, (s.visitors / srcMax) * 100)}%` }} /></div>
            </div>
          ))}
        </Card>

        <Card title="AI answer engines" sub="visitors sent by ChatGPT, Perplexity, Gemini, Claude and Copilot">
          {data.ai.length === 0 ? <div className="sa-none">No visitors from AI assistants yet.</div> : data.ai.map((a) => (
            <div key={a.engine} className="sa-ai">
              <div className="sa-src-h"><b>{a.engine}</b><span>{N(a.visitors)} visitors · {N(a.events)} events</span></div>
              <div className="sa-mini-h">Pages they land on</div>
              {a.landing.map((l) => <div key={l.path} className="sa-city"><span className="sa-mono">{l.path}</span><b>{l.n}</b></div>)}
            </div>
          ))}
        </Card>
      </div>

      <div className="sa-grid3">
        <Card title="Button clicks" sub="named actions by where they were clicked">
          {ctaRows.length === 0 ? <div className="sa-none">No tracked clicks yet.</div> : (
            <table className="sa-tbl"><thead><tr><th>Button</th><th>Where</th><th>Clicks</th><th>Visitors</th></tr></thead>
              <tbody>{ctaRows.map((c, i) => <tr key={i}><td><b>{CTA_LABEL[c.cta] || c.cta}</b></td><td>{c.placement || '—'}</td><td>{N(c.clicks)}</td><td>{N(c.visitors)}</td></tr>)}</tbody></table>
          )}
          {data.buttons.length > 0 && <>
            <div className="sa-mini-h" style={{ marginTop: 14 }}>Most-clicked elements (all clicks)</div>
            {data.buttons.slice(0, 8).map((b) => <div key={b.text} className="sa-city"><span>“{b.text}”</span><b>{N(b.clicks)}</b></div>)}
          </>}
        </Card>

        <Card title="Demo film" sub="the /watch page · distinct visitors">
          {['play', 'unmute', 'watched_25', 'watched_50', 'watched_75', 'watched_100', 'zoom', 'share', 'download'].map((a) => (
            <div key={a} className="sa-vrow"><span>{VIDEO_LABEL[a]}</span><div className="sa-vbar"><div style={{ width: `${pct(videoMap[a]?.visitors || 0, videoMap.play?.visitors || 0)}%` }} /></div><b>{N(videoMap[a]?.visitors || 0)}</b></div>
          ))}
        </Card>

        <Card title="Top pages" sub="page views">
          {data.pages.length === 0 ? <div className="sa-none">No page views yet.</div> : data.pages.map((p) => <div key={p.path} className="sa-city"><span className="sa-mono">{p.path}</span><b>{N(p.views)}</b></div>)}
        </Card>
      </div>

      <Card title="Contact form" sub={`${N(formStats.started)} started · ${N(formStats.submitted)} submitted · ${N(formStats.left)} left before sending · we record which fields were filled, never what was typed`}>
        {data.forms.length === 0 ? <div className="sa-none">Nobody has started the contact form in this period.</div> : (
          <div className="sa-scroll"><table className="sa-tbl">
            <thead><tr><th>Visitor</th><th>Location</th><th>IP</th><th>Fields filled</th><th>Completion</th><th>Status</th><th>When</th></tr></thead>
            <tbody>{data.forms.map((x) => (
              <tr key={x.person_id} className="sa-click" onClick={() => openPerson({ ...x })}>
                <td><b>{x.name || 'Visitor'}</b>{x.email && <div className="sa-mono sa-dim">{x.email}</div>}</td>
                <td><Where r={x} /></td>
                <td className="sa-mono">{x.ip || '—'}</td>
                <td>{x.fields.length ? x.fields.join(', ') : '—'}</td>
                <td><div className="sa-prog"><div style={{ width: `${x.completion}%` }} /></div> {x.completion}%</td>
                <td><span className={`sa-tag ${x.submitted ? 'ok' : x.abandoned ? 'warn' : ''}`}>{x.submitted ? 'Submitted' : x.abandoned ? 'Left without sending' : 'In progress'}</span></td>
                <td className="sa-dim">{ago(x.last)}</td>
              </tr>))}</tbody>
          </table></div>
        )}
      </Card>

      <Card title="Visitors" right={<span className="sa-dim">click a visitor to see everything they did</span>}>
        {!users ? <div className="sa-none">Loading…</div> : users.rows.length === 0 ? <div className="sa-none">No visitors yet.</div> : (
          <>
            <div className="sa-scroll"><table className="sa-tbl">
              <thead><tr><th>Visitor</th><th>IP address</th><th>Location</th><th>Source</th><th>Device</th><th>Pages</th><th>Demo</th><th>Clicks</th><th>Form</th><th>Last seen</th></tr></thead>
              <tbody>{users.rows.map((u) => (
                <tr key={u.person_id} className="sa-click" onClick={() => openPerson(u)}>
                  <td><b>{u.name || (u.email ? u.email.split('@')[0] : 'Visitor')}</b><div className="sa-dim">{u.email || 'anonymous'}</div></td>
                  <td className="sa-mono">{u.ip || '—'}</td>
                  <td><Where r={u} /></td>
                  <td>{u.source}</td>
                  <td>{[u.device, u.browser].filter(Boolean).join(' · ') || '—'}</td>
                  <td><b>{N(u.pageviews)}</b></td>
                  <td>{u.demo_clicks ? <span className="sa-tag ok">{u.played ? 'Watched' : 'Clicked'}</span> : '—'}</td>
                  <td>{N(u.cta_clicks)}</td>
                  <td>{u.form ? <span className={`sa-tag ${u.form === 'submitted' ? 'ok' : 'warn'}`}>{u.form}</span> : '—'}</td>
                  <td className="sa-dim">{ago(u.last)}</td>
                </tr>))}</tbody>
            </table></div>
            <div className="sa-pager">
              <span>Showing {(users.page - 1) * users.size + 1}–{(users.page - 1) * users.size + users.rows.length} of {N(users.total)}</span>
              <div><button disabled={users.page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button><span>Page {users.page} of {Math.max(1, Math.ceil(Math.min(users.total, users.listed || users.total) / users.size))}</span><button disabled={users.page * users.size >= Math.min(users.total, users.listed || users.total)} onClick={() => setPage((p) => p + 1)}>Next ›</button></div>
            </div>
          </>
        )}
      </Card>

      {person && (
        <div className="sa-drawer-bg" onClick={() => setPerson(null)}>
          <aside className="sa-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="sa-drawer-h">
              <div><h3>{person.row.name || person.row.email || 'Anonymous visitor'}</h3>
                <p className="sa-mono sa-dim">{person.row.ip || ''}</p>
                <p className="sa-dim">{place(person.row)}{person.row.source ? ` · came from ${person.row.source}` : ''}</p></div>
              <button className="sa-x" onClick={() => setPerson(null)}>×</button>
            </div>
            <LocationPanel row={person.row} />
            <div className="sa-mini-h" style={{ marginTop: 18 }}>Activity</div>
            {!person.events ? <div className="sa-none">Loading activity…</div> : person.events.length === 0 ? <div className="sa-none">No activity found.</div> : (
              <ol className="sa-timeline">
                {person.events.filter((e) => e.event !== '$web_vitals' && e.event !== '$set').map((e, i) => (
                  <li key={i} className={/contact_form|watch_demo|cta_click|demo_video/.test(e.event) ? 'hi' : ''}>
                    <span className="sa-t">{ago(e.timestamp)}</span><span className="sa-e">{EVENT_LABEL(e)}</span>
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

export const siteAnalyticsCss = `
.sa{ display:flex; flex-direction:column; gap:18px; }
.sa-toolbar{ display:flex; justify-content:space-between; align-items:flex-end; gap:16px; flex-wrap:wrap; }
.sa-title{ font-size:22px; font-weight:800; letter-spacing:-0.03em; margin:0; }
.sa-sub{ color:#6B6862; font-size:13px; margin:4px 0 0; }
.sa-tools{ display:flex; gap:8px; }
.sa-select,.sa-refresh{ border:1px solid #E7E1D6; background:#fff; border-radius:999px; padding:8px 14px; font:inherit; font-size:13px; font-weight:600; color:#111; cursor:pointer; }
.sa-refresh{ background:#111; color:#fff; border-color:#111; }
.sa-kpis{ display:grid; grid-template-columns:repeat(6,1fr); gap:12px; }
.sa-kpi{ background:#fff; border:1px solid #E7E1D6; border-radius:16px; padding:16px 18px; display:flex; flex-direction:column; gap:4px; }
.sa-kpi span{ font-size:12.5px; color:#6B6862; font-weight:600; }
.sa-kpi b{ font-size:30px; font-weight:800; letter-spacing:-0.04em; line-height:1.15; }
.sa-kpi em{ font-style:normal; font-size:11.5px; color:#9C978C; }
.sa-grid2{ display:grid; grid-template-columns:1fr 1fr; gap:18px; }
.sa-grid3{ display:grid; grid-template-columns:1.2fr 1fr 1fr; gap:18px; }
.sa-card{ background:#fff; border:1px solid #E7E1D6; border-radius:18px; padding:20px 22px; min-width:0; }
.sa-card-h{ display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:14px; }
.sa-card-h h3{ font-size:15.5px; font-weight:800; margin:0; letter-spacing:-0.02em; }
.sa-card-h p{ font-size:12px; color:#9C978C; margin:3px 0 0; }
.sa-none{ color:#9C978C; font-size:13px; padding:14px 0; }
.sa-empty{ background:#fff; border:1px solid #E7E1D6; border-radius:18px; padding:40px; text-align:center; color:#6B6862; }
.sa-funnel{ display:flex; flex-direction:column; gap:10px; }
.sa-frow{ display:grid; grid-template-columns:190px 1fr 44px; align-items:center; gap:12px; font-size:13px; }
.sa-flabel{ color:#3A3A37; }
.sa-fbar{ background:#F3EEE6; border-radius:999px; height:28px; overflow:hidden; }
.sa-fbar > div{ background:#111; height:100%; border-radius:999px; display:flex; align-items:center; padding-left:10px; min-width:34px; }
.sa-fbar b{ color:#fff; font-size:12px; }
.sa-fpct{ text-align:right; color:#6B6862; font-size:12.5px; }
.sa-geo{ display:grid; grid-template-columns:200px 1fr; gap:18px; align-items:center; }
.sa-donut-n{ font:800 26px 'Space Grotesk',system-ui; fill:#111; }
.sa-donut-l{ font:500 11px 'Space Grotesk',system-ui; fill:#9C978C; }
.sa-geo-list{ display:flex; flex-direction:column; gap:2px; }
.sa-geo-row{ display:grid; grid-template-columns:12px 1fr auto 44px; gap:10px; align-items:center; background:none; border:none; padding:6px 8px; border-radius:8px; font:inherit; font-size:13.5px; text-align:left; cursor:pointer; color:#111; }
.sa-geo-row:hover,.sa-geo-row.on{ background:#FAF7F1; }
.sa-geo-row i{ width:11px; height:11px; border-radius:3px; display:block; }
.sa-geo-row em{ font-style:normal; color:#9C978C; text-align:right; font-size:12px; }
.sa-note{ font-size:12px; color:#9C978C; border-top:1px solid #EFE9DF; padding-top:10px; margin:12px 0 0; }
.sa-cities{ border-top:1px solid #EFE9DF; margin-top:12px; padding-top:10px; }
.sa-cities-h,.sa-mini-h{ font-size:11px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:#9C978C; margin-bottom:6px; }
.sa-city{ display:flex; justify-content:space-between; gap:10px; font-size:13px; padding:5px 0; border-bottom:1px dashed #EFE9DF; }
.sa-city:last-child{ border-bottom:none; }
.sa-src{ margin-bottom:12px; }
.sa-src-h{ display:flex; justify-content:space-between; gap:10px; font-size:13.5px; margin-bottom:5px; flex-wrap:wrap; }
.sa-src-h span{ color:#6B6862; font-size:12.5px; }
.sa-src-bar{ background:#F3EEE6; height:7px; border-radius:99px; overflow:hidden; }
.sa-src-bar > div{ background:#111; height:100%; border-radius:99px; }
.sa-ai{ margin-bottom:14px; }
.sa-vrow{ display:grid; grid-template-columns:140px 1fr 36px; gap:10px; align-items:center; font-size:13px; padding:4px 0; }
.sa-vbar{ background:#F3EEE6; height:8px; border-radius:99px; overflow:hidden; }
.sa-vbar > div{ background:#0F6E56; height:100%; border-radius:99px; }
.sa-vrow b{ text-align:right; }
.sa-tbl{ width:100%; border-collapse:collapse; font-size:13px; }
.sa-tbl th{ text-align:left; font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:#9C978C; padding:8px 10px; border-bottom:1px solid #E7E1D6; white-space:nowrap; }
.sa-tbl td{ padding:10px; border-bottom:1px solid #F3EEE6; vertical-align:top; }
.sa-click{ cursor:pointer; } .sa-click:hover td{ background:#FAF7F1; }
.sa-scroll{ overflow-x:auto; }
.sa-mono{ font-family:ui-monospace,Menlo,monospace; font-size:12px; word-break:break-all; }
.sa-dim{ color:#9C978C; font-size:12px; }
.sa-tag{ display:inline-block; font-size:11px; font-weight:700; padding:2px 9px; border-radius:99px; background:#F3EEE6; color:#3A3A37; white-space:nowrap; }
.sa-tag.ok{ background:#E3F2EC; color:#0F6E56; } .sa-tag.warn{ background:#FFF3D6; color:#8A5A00; }
.sa-prog{ display:inline-block; width:60px; height:7px; background:#F3EEE6; border-radius:99px; overflow:hidden; vertical-align:middle; margin-right:6px; }
.sa-prog > div{ height:100%; background:#111; }
.sa-pager{ display:flex; justify-content:space-between; align-items:center; gap:10px; margin-top:12px; font-size:13px; color:#6B6862; flex-wrap:wrap; }
.sa-pager div{ display:flex; gap:10px; align-items:center; }
.sa-pager button{ border:1px solid #E7E1D6; background:#fff; border-radius:999px; padding:6px 14px; font:inherit; font-weight:600; cursor:pointer; }
.sa-pager button:disabled{ opacity:.4; cursor:default; }
.sa-drawer-bg{ position:fixed; inset:0; background:rgba(17,17,17,.25); z-index:50; display:flex; justify-content:flex-end; }
.sa-drawer{ width:min(500px,100%); height:100%; background:#fff; border-left:1px solid #E7E1D6; padding:22px; overflow-y:auto; box-shadow:-12px 0 30px rgba(0,0,0,.08); }
.sa-drawer-h{ display:flex; justify-content:space-between; gap:12px; margin-bottom:16px; }
.sa-drawer-h h3{ margin:0 0 4px; font-size:18px; font-weight:800; }
.sa-drawer-h p{ margin:2px 0; }
.sa-x{ border:none; background:#F3EEE6; width:34px; height:34px; border-radius:50%; font-size:20px; cursor:pointer; }
.sa-timeline{ list-style:none; margin:0; padding:0 0 0 14px; border-left:2px solid #EFE9DF; }
.sa-timeline li{ position:relative; padding:8px 0 8px 12px; font-size:13px; }
.sa-timeline li::before{ content:''; position:absolute; left:-20px; top:13px; width:10px; height:10px; border-radius:50%; background:#D9D2C5; }
.sa-timeline li.hi::before{ background:#111; }
.sa-timeline li.hi .sa-e{ font-weight:700; }
.sa-t{ display:block; font-size:11.5px; color:#9C978C; }
.sa-loc{ border:1px solid #E7E1D6; border-radius:14px; padding:14px; background:#FCFAF6; }
.sa-loc-grid{ display:grid; grid-template-columns:128px 1fr; gap:6px 12px; margin:6px 0 0; font-size:13px; }
.sa-loc-grid dt{ color:#9C978C; }
.sa-loc-grid dd{ margin:0; color:#111; font-weight:600; word-break:break-word; }
.sa-loc-warn{ background:#FFF3D6; color:#8A5A00; font-size:12px; border-radius:10px; padding:8px 10px; margin:6px 0 8px; }
.sa-map{ width:100%; height:200px; border:1px solid #E7E1D6; border-radius:12px; margin-top:12px; display:block; }
.sa-loc-foot{ display:flex; flex-direction:column; gap:6px; font-size:11.5px; color:#9C978C; margin-top:8px; }
.sa-loc-foot a{ color:#111; font-weight:700; text-decoration:none; }
@media (max-width:1200px){ .sa-kpis{ grid-template-columns:repeat(3,1fr); } .sa-grid3{ grid-template-columns:1fr 1fr; } }
@media (max-width:860px){ .sa-grid2,.sa-grid3{ grid-template-columns:1fr; } .sa-kpis{ grid-template-columns:repeat(2,1fr); } .sa-geo{ grid-template-columns:1fr; justify-items:center; } .sa-frow{ grid-template-columns:130px 1fr 40px; } }
`;
